import { FC } from 'react';
import styled from 'styled-components';
import { IconKey } from '../SvgIcons/svgIconsParams';
import { SvgIcon } from '../SvgIcons/svgIcons';

type CommonProps = {
  /** Text decoration to show an underlined link. Defaults to false. **/
  underline?: boolean;
  hoverUnderline?: boolean;
  bold?: boolean;
  leftSideIcon?: IconKey;
  rightSideIcon?: IconKey;
  /** ARIA label for accessibility */
  ariaLabel?: string;
  /** Whether the element is disabled */
  disabled?: boolean;
  color?: string;
  hoverColor?: string;
};

type ButtonProps = {
  as?: 'button';
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'submit' | 'reset' | 'button';
};

type LinkProps = {
  as: 'a';
  href: string;
  target?: '_blank' | '_self' | '_parent' | '_top';
  rel?: string;
};

type Props = CommonProps & (ButtonProps | LinkProps);

const StyledLinkButton = styled.button<CommonProps>`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.designToken.spacing1};
  align-items: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  padding: 0;
  margin: 0;
  color: ${({ theme, disabled, color }) =>
    disabled ? theme.vars.newColor.darkGS3 : theme.vars.newColor.blue2 || color ? color : theme.vars.newColor.blue2};
  background-color: transparent;
  border: 0;
  font-weight: ${({ bold }) => (bold ? 'bold' : 'normal')};
  text-decoration: ${({ underline }) => (underline ? 'underline' : 'none')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  :hover {
    color: ${({ theme, hoverColor }) => (hoverColor ? hoverColor : theme.vars.newColor.blue1)};
    text-decoration: ${({ hoverUnderline }) => (hoverUnderline ? 'underline' : 'none')};
  }
  :hover path {
    fill: ${({ theme }) => theme.vars.newColor.blue1};
  }
  & path {
    fill: ${({ theme }) => theme.vars.newColor.blue2};
  }
`;

export const LinkButton: FC<Props> = ({
  ariaLabel,
  hoverUnderline,
  underline,
  bold,
  leftSideIcon,
  rightSideIcon,
  children,
  as = 'button',
  disabled,
  ...rest
}) => (
  <StyledLinkButton
    data-testid="LinkButton"
    as={as}
    aria-label={ariaLabel}
    hoverUnderline={hoverUnderline}
    underline={underline}
    bold={bold}
    disabled={disabled}
    aria-disabled={disabled || undefined}
    {...rest}
  >
    {leftSideIcon && <SvgIcon name={leftSideIcon} size="24" />}
    {children}
    {rightSideIcon && <SvgIcon name={rightSideIcon} size="24" />}
  </StyledLinkButton>
);
